import { useCallback, useMemo, useEffect, type FC } from 'react';
import { useRouter } from 'next/navigation';
import type { PhoneLoginProps } from './phone-login.types';
import { PhoneLoginView } from './phone-login-view';
import { useAuth } from '../../hooks';
import { AuthSuccessContext } from '../../contexts/auth-success.context';
import { AuthSuccessView } from '../auth-success-view';
import { resetToPhoneEntry } from '../../stores/auth.store';

export const PhoneLogin: FC<PhoneLoginProps> = ({
    LoginForm,
    AuthenticationFaqComponent,
    OtpChannelSelector,
    OtpVerificationComponent,
    RegisterForm,
    onAuthSuccess,
}) => {
    const { currentStep } = useAuth();
    const router = useRouter();
    const authSuccessValue = useMemo(() => ({ onAuthSuccess }), [onAuthSuccess]);

    const backAction = useCallback(() => {
        resetToPhoneEntry();
    }, []);

    useEffect(() => {
        resetToPhoneEntry();
    }, []);

    // Fallback handler for standalone auth page (no modal onAuthSuccess callback)
    const handleAuthSuccessComplete = useCallback(() => {
        if (onAuthSuccess) {
            onAuthSuccess();
        } else {
            router.push('/');
        }
    }, [onAuthSuccess, router]);

    const renderContent = useCallback(() => {
        switch (currentStep) {
            case 'EnterPhoneNumber':
                return <LoginForm />;
            case 'SelectChannel':
                return <OtpChannelSelector />;
            case 'VerifyOtp':
                return <OtpVerificationComponent />;
            case 'Register':
                return <RegisterForm />;
            case 'Faq':
                return <AuthenticationFaqComponent />;
            default:
                return null;
        }
    }, [
        currentStep,
        LoginForm,
        OtpChannelSelector,
        OtpVerificationComponent,
        RegisterForm,
        AuthenticationFaqComponent,
    ]);

    // When auth succeeds, render the success celebration view
    // This completely replaces the login form UI for a seamless transition
    if (currentStep === 'AuthSuccess') {
        return (
            <AuthSuccessContext.Provider value={authSuccessValue}>
                <AuthSuccessView onComplete={handleAuthSuccessComplete} />
            </AuthSuccessContext.Provider>
        );
    }

    return (
        <AuthSuccessContext.Provider value={authSuccessValue}>
            <PhoneLoginView headerAction={backAction} renderComponent={renderContent} />
        </AuthSuccessContext.Provider>
    );
};
